import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Button from '../atoms/Button';

// PR-UX-10 confirm-undo · destructive actions must require explicit confirmation,
// destructive button distance ≥ neutral button to prevent slip.
// PR-UX-10 flow · escapable via Escape; doesn't break critical flows mid-task.
interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}

export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
  busy = false,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus(); // PR-UX-10 · cancel is default focus, prevents accidental confirm
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className="fixed inset-0 z-[100] flex items-center justify-center px-6 bg-ink/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="bg-paper border border-hairline rounded-lg shadow-2xl max-w-md w-full p-8">
        <h2 id="confirm-title" className="font-serif text-2xl font-medium mb-3">
          {title}
        </h2>
        {body && <p className="text-base text-ink-2 mb-6">{body}</p>}
        <div className="flex gap-3 justify-end">
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'vermilion' : 'primary'}
            onClick={onConfirm}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
